<template>
	<div class="edit-row edit-row--b-top">
		<template v-if="!isContentLinkBlocked">
			<div class="edit-row__label">{{ t('quickEditBar.linkNode') }}</div>
			<field-regular
				:placeholder="t('popup.content.label.link')"
				:label-inline="true"
				:field-item-grow="true"
				:field-item-classname="'mod--edit-bar'"
				:disabled="blocked"
				v-model="nodeLink"
				@onChange="updateLang"
			></field-regular>
			<deeplink-pick-button
				:disabled="blocked"
				@picked="onDeeplinkPicked"
			></deeplink-pick-button>
		</template>
		<template v-else>
			<div class="edit-row__message">
				<i class="fas fa-exclamation-triangle"></i>
				<span>{{ t('quickEditBar.linkBlocked') }}</span>
			</div>
		</template>
	</div>
</template>

<script>
import {useI18n} from 'vue-i18n';
import {computed} from 'vue';
import {useStore} from 'vuex';
import FieldRegular from '@/components/fields/FieldRegular';
import useTranslations from "@/utils/language/useTranslations";
import {DeeplinkPickButton} from '@/modules/editor-link';

export default {
	components: {
		FieldRegular,
		DeeplinkPickButton
	},
	props: {
		blocked: {
			type: Boolean,
			default: false
		}
	},
	setup() {
		const {t} = useI18n();
		const store = useStore();
		const language = computed(() => store.getters["maps/languages/getLanguage"].id);
		const currentLang = computed(() => store.getters["maps/languages/getLanguageLocal"].id);
		const nodeOriginalActive = computed(() => store.getters["nodes/getSelectedRawNode"]);
		const nodesOriginal = computed(() => store.getters["nodes/getRawNodes"]);
		const nodeActiveID = nodeOriginalActive.value.id;
		const {composeIntoLang, getSubvariants, replaceLang} = useTranslations();
		function updateLang(value){
			if(!value.trim()) return;
			const nodeOriginal = nodesOriginal.value.find(node => node.id === nodeActiveID);
			if(currentLang.value === language.value) {
				// update node_link_to in additional languages
				const result = composeIntoLang({value, replace: true, field: 'node_link_to', currentLang: currentLang.value, langObj: nodeOriginal.content.lang})
				// default language is a part of the result, thus ensure the length of lang object is more than 1 before making request
				if(Object.keys(result.lang).length > 1){
					nodeUpdateContent(result, nodeActiveID);
					nodeSaveContent(result, nodeActiveID);
				}
				return;
			}
			// if the current lang isn't a default language,
			// check if there are subvariants of the current language and update them accordingly
			const subvariants = getSubvariants({id: currentLang.value, lang: nodeOriginal.content.lang});
			if(subvariants.length && currentLang.value !== language.value){
				const result = {lang: {[currentLang.value]: {node_link_to: value}}}
				subvariants.forEach(lang => {
					if(!nodeOriginal.content.lang[lang]['node_link_to'] && !nodeOriginal.content.lang[lang].translated){
						Object.assign(result.lang, {[lang]: {node_link_to: replaceLang(value, lang)}})
					}
				})
				nodeUpdateContent(result, nodeActiveID);
				nodeSaveContent(result, nodeActiveID);
			}
		}
		
		const currentNode = computed(() => store.getters['nodes/getSelectedNode']);
		const isContentLinkBlocked = computed(() => currentNode.value.contentLinkBlocked);
		
		const currentNodeOriginal = computed(() => store.getters['nodes/getSelectedRawNode']);
		const nodeLink = computed({
			get(){return currentNodeOriginal.value.content.lang[currentLang.value].node_link_to},
			set(value){
				const result = {lang: {}};
				// collect all languages node_link_to values to compose blocked content/link correct
				for(const [lang, object] of Object.entries(currentNodeOriginal.value.content.lang)){
					result.lang[lang] = {node_link_to: object.node_link_to}
				}
				result.lang[currentLang.value] = {node_link_to: value};
				nodeUpdateContent(result);
				nodeSaveContent(result)
			}
		});
		
		function nodeUpdateContent(content, id){
			if(id === undefined) id = currentNode.value.id;
			store.dispatch('nodes/content/nodeUpdateContent', {id, content});
		}
		async function nodeSaveContent(content, id){
			if(id === undefined) id = currentNode.value.id;
			await store.dispatch('nodes/content/nodeSaveContent', {id, content});
		}
		
		// Picking on the map takes the same route as typing: the setter saves the
		// value, updateLang carries it into the other languages.
		function onDeeplinkPicked(href){
			nodeLink.value = href;
			updateLang(href);
		}

		return {
			t,
			nodeLink,
			isContentLinkBlocked,
			updateLang,
			onDeeplinkPicked
		}
	}
}
</script>

<style lang="scss">

</style>
